import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CanvasItem } from '~/components/chat/message'
import { Button } from '~/components/common/Button'
import { CustomTooltip } from '~/components/common/CustomTooltip'
import {
  downloadBlob,
  generateDocxBlob,
  generatePdfBlob,
  markdownToHtml,
  preprocessContent
} from '~/components/common/dropdowns/DownloadDocumentDropdown'
import {
  SelectorDropdownContent,
  SelectorDropdownItem
} from '~/components/common/dropdowns/SelectorDropdown'
import { DownloadProgressButton } from '~/components/common/dropdowns/DownloadProgressButton'
import { HoverText } from '~/components/common/HoverText'
import { PopoverNavDownIcon } from '~/components/icons'
import {
  SegmentControls,
  SegmentControlsList,
  SegmentControlsTrigger
} from '~/components/common/SegmentControls'
import { Typography } from '~/components/common/Typography'
import { useGlobalStreaming } from '~/contexts/GlobalStreamingContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { readEditableCanvasContent, saveCanvasContent } from '~/services/canvasService'
import type { BaseSelectionContext, CanvasSelectionContext } from '~/types/canvas'
import { BetterToast } from '~/utils/toast'
import { CanvasCodeEditor } from './CanvasCodeEditor'
import { buildCanvasDownloadFilename, resolveCanvasDocumentMeta } from './canvasDocumentMeta'
import { CanvasEditor } from './CanvasEditor'
import {
  CANVAS_HTML_SANDBOX,
  downloadRenderedCanvasHtmlPdf,
  openSandboxedCanvasHtmlInNewTab
} from './canvasHtmlExport'

// Re-export the shared type for consumers
export type { CanvasSelectionContext } from '~/types/canvas'
export { buildSandboxedCanvasWrapper } from './canvasHtmlExport'

const FILE_TYPE_LABELS: Record<string, string> = {
  md: 'Markdown',
  markdown: 'Markdown',
  json: 'JSON',
  xml: 'XML',
  xsd: 'XML Schema',
  yaml: 'YAML',
  yml: 'YAML',
  csv: 'CSV',
  html: 'HTML',
  htm: 'HTML',
  css: 'CSS',
  js: 'JavaScript',
  jsx: 'JavaScript',
  ts: 'TypeScript',
  tsx: 'TypeScript',
  py: 'Python',
  java: 'Java',
  sql: 'SQL',
  sh: 'Shell',
  bash: 'Shell',
  txt: 'Text',
  dwl: 'DataWeave',
  dataweave: 'DataWeave',
  wsdl: 'WSDL'
}

function getFileTypeLabel(extension: string): string {
  const ext = extension.toLowerCase()
  const label = FILE_TYPE_LABELS[ext] || ext.toUpperCase()
  return `${label} (.${ext})`
}

function getSourceFileTypeLabel(extension: string): string {
  const ext = extension.toLowerCase()
  const label = FILE_TYPE_LABELS[ext] || ext.toUpperCase()
  return `${label} Source (.${ext})`
}

interface EditableCanvasSplitScreenProps {
  canvas: CanvasItem
  onClose: () => void
  /** Callback when user submits feedback on selected text with full canvas context */
  onSelectionFeedback?: (feedback: string, context: CanvasSelectionContext) => void
  /** Whether the canvas is currently being processed by the agent */
  isProcessing?: boolean
}

type DownloadAction = () => void | Promise<void>
type DownloadMenuContext = 'html-header' | 'editor'
type DownloadMenuActionId =
  | 'download-rendered-pdf'
  | 'download-source-pdf'
  | 'download-original'
  | 'download-docx'
type DownloadMenuItemContext = {
  context: DownloadMenuContext
  documentMeta: ReturnType<typeof resolveCanvasDocumentMeta>
}

interface DownloadMenuItemDefinition {
  id: string
  contexts: readonly DownloadMenuContext[]
  actionId: DownloadMenuActionId
  label: (context: DownloadMenuItemContext) => string
  isVisible: (context: DownloadMenuItemContext) => boolean
}

// Reorder this list to change the download menu order. Each entry owns the
// contexts where it appears plus its visibility and label rules.
const DOWNLOAD_MENU_ITEMS = [
  {
    id: 'rendered-pdf',
    contexts: ['html-header'],
    actionId: 'download-rendered-pdf',
    label: () => 'Render as PDF (.pdf)',
    isVisible: ({ documentMeta }) => documentMeta.mode === 'html'
  },
  {
    id: 'source-pdf',
    contexts: ['editor'],
    actionId: 'download-source-pdf',
    label: () => 'PDF Document (.pdf)',
    isVisible: ({ documentMeta }) => documentMeta.mode !== 'unsupported'
  },
  {
    id: 'original',
    contexts: ['html-header', 'editor'],
    actionId: 'download-original',
    label: ({ context, documentMeta }) =>
      context === 'html-header'
        ? getSourceFileTypeLabel(documentMeta.extension)
        : getFileTypeLabel(documentMeta.extension),
    isVisible: () => true
  },
  {
    id: 'docx',
    contexts: ['html-header', 'editor'],
    actionId: 'download-docx',
    label: () => 'Microsoft Word (.docx)',
    isVisible: ({ documentMeta }) => documentMeta.mode !== 'unsupported'
  }
] as const satisfies readonly DownloadMenuItemDefinition[]

const DOWNLOAD_MENU_CONTEXT_BY_DOCUMENT_MODE: Partial<
  Record<ReturnType<typeof resolveCanvasDocumentMeta>['mode'], DownloadMenuContext>
> = {
  html: 'html-header'
}

function getDownloadMenuContext(
  mode: ReturnType<typeof resolveCanvasDocumentMeta>['mode']
): DownloadMenuContext {
  return DOWNLOAD_MENU_CONTEXT_BY_DOCUMENT_MODE[mode] ?? 'editor'
}

interface DownloadMenuItemConfig {
  id: string
  label: string
  onClick: DownloadAction
}

interface DownloadDropdownOptions {
  context: DownloadMenuContext
  buttonClassName?: string
}

/**
 * Split-screen editor for editable canvas items.
 * Displays the TipTap editor on the right side of the chat with:
 * - Header with title, save status, and actions
 * - Auto-save with debounce (2.5 seconds after last edit)
 * - Download functionality for current content
 */
export function EditableCanvasSplitScreen({
  canvas,
  onClose,
  onSelectionFeedback,
  isProcessing = false
}: EditableCanvasSplitScreenProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const { canvasRefetchTrigger } = useGlobalStreaming()
  const [content, setContent] = useState<string>('')
  const [loadedContentType, setLoadedContentType] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // HTML canvases default to the live sandboxed preview. Users can flip to
  // the source view to edit; the toggle is only rendered when documentMeta
  // reports mode === 'html' (see header below).
  const [htmlViewMode, setHtmlViewMode] = useState<'rendered' | 'source'>('rendered')

  // Reset the HTML toggle whenever we switch to a different canvas so the
  // previous canvas's "source" selection never leaks across.
  useEffect(() => {
    setHtmlViewMode('rendered')
  }, [canvas.s3FilePath, canvas.title])

  const documentMeta = useMemo(
    () => resolveCanvasDocumentMeta({ canvas, contentType: loadedContentType }),
    [canvas, loadedContentType]
  )

  // Track the original content to detect actual changes
  const originalContentRef = useRef<string>('')
  // Track the current content for download
  const currentContentRef = useRef<string>('')
  const htmlPreviewFrameRef = useRef<HTMLIFrameElement | null>(null)
  const lastHtmlPreviewWidthRef = useRef<number>()
  // Track unsaved changes for use in effects (to avoid stale closures)
  const hasUnsavedChangesRef = useRef(false)

  // Keep ref in sync with state
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges
  }, [hasUnsavedChanges])

  // Load content from S3 - shared function for initial load and refetch
  const loadContent = useCallback(async () => {
    // Skip refetch if user has unsaved changes to prevent data loss
    if (hasUnsavedChangesRef.current) {
      console.log('Skipping canvas refetch - user has unsaved changes')
      return
    }

    setIsLoading(true)
    setLoadError(null)

    try {
      let loadedContent = ''
      let contentType = ''

      if (
        canvas.canvasType === 'CANVAS_TYPE_S3_FILE' &&
        canvas.s3FilePath &&
        currentWorkspace?.uuid
      ) {
        // Fetch editable content from S3 via API
        const response = await readEditableCanvasContent(currentWorkspace.uuid, canvas.s3FilePath)
        loadedContent = response.content
        contentType = response.contentType
      } else if (canvas.canvasType === 'CANVAS_TYPE_MARKDOWN') {
        // Use inline content for markdown
        loadedContent = canvas.content || ''
        contentType = 'text/markdown'
      } else {
        // Use inline content as fallback
        loadedContent = canvas.content || ''
        contentType = ''
      }

      // Store original content for change detection
      originalContentRef.current = loadedContent
      currentContentRef.current = loadedContent
      setContent(loadedContent)
      setLoadedContentType(contentType)
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('Failed to load canvas content:', error)
      setLoadError(error instanceof Error ? error.message : 'Failed to load content')
    } finally {
      setIsLoading(false)
    }
  }, [canvas.canvasType, canvas.s3FilePath, canvas.content, currentWorkspace?.uuid])

  // Initial content load
  useEffect(() => {
    loadContent()
  }, [loadContent])

  // Refetch when SSE arrives for this canvas (direct trigger from context)
  useEffect(() => {
    if (canvasRefetchTrigger && canvasRefetchTrigger.s3FilePath === canvas.s3FilePath) {
      loadContent()
    }
  }, [canvasRefetchTrigger, canvas.s3FilePath, loadContent])

  // Auto-save with debounce (2.5 seconds after last edit)
  const debouncedSave = useMemo(
    () =>
      debounce(async (newContent: string) => {
        if (!canvas.s3FilePath || !currentWorkspace?.uuid) {
          console.warn('Cannot save: missing s3FilePath or workspace')
          return
        }

        // Skip save if content hasn't changed from original
        if (newContent === originalContentRef.current) {
          setHasUnsavedChanges(false)
          return
        }

        setIsSaving(true)
        try {
          if (documentMeta.mode === 'unsupported') {
            return
          }
          await saveCanvasContent(currentWorkspace.uuid, {
            s3Path: canvas.s3FilePath,
            content: newContent,
            contentType: documentMeta.saveContentType
          })
          setHasUnsavedChanges(false)
          // Update original content reference after successful save
          originalContentRef.current = newContent
        } catch (error) {
          console.error('Failed to save canvas:', error)
          BetterToast.error('Failed to save changes. Will retry automatically.')
        } finally {
          setIsSaving(false)
        }
      }, 2500),
    [canvas.s3FilePath, currentWorkspace?.uuid, documentMeta.mode, documentMeta.saveContentType]
  )

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel()
    }
  }, [debouncedSave])

  // Handle content changes from editor
  const handleContentChange = useCallback(
    (newContent: string) => {
      currentContentRef.current = newContent
      setContent(newContent)

      // Check if content has actually changed
      if (newContent !== originalContentRef.current) {
        setHasUnsavedChanges(true)
        debouncedSave(newContent)
      }
    },
    [debouncedSave]
  )

  const getHtmlPreviewRenderWidth = useCallback(() => {
    const width = htmlPreviewFrameRef.current?.getBoundingClientRect().width
    if (width && Number.isFinite(width)) {
      lastHtmlPreviewWidthRef.current = width
      return width
    }
    return lastHtmlPreviewWidthRef.current
  }, [])

  // Open the current HTML payload in a new browser tab so the user can view
  // it at full window width when the side panel feels cramped.
  //
  // SECURITY: see buildSandboxedCanvasWrapper below. Without the wrapper,
  // the agent's HTML would run at this app's origin (blob: inherits the
  // creator's origin) and could read document.cookie / localStorage / make
  // credentialed fetches. The wrapper contains a sandboxed iframe so the
  // agent code runs with an opaque origin, identical to the in-panel view.
  const handleOpenHtmlInNewTab = useCallback(() => {
    if (documentMeta.mode !== 'html') return
    const html = currentContentRef.current || content
    if (!html) return
    try {
      if (
        !openSandboxedCanvasHtmlInNewTab(html, {
          onDownloadPdf: async () => {
            await downloadRenderedCanvasHtmlPdf(html, {
              filename: documentMeta.filename,
              renderWidth: getHtmlPreviewRenderWidth()
            })
          }
        })
      ) {
        BetterToast.error(
          'Popup blocked. Allow popups for this site to open the canvas in a new tab.'
        )
      }
    } catch (e) {
      console.error('Failed to open HTML in new tab:', e)
      BetterToast.error('Could not open the HTML in a new tab.')
    }
  }, [documentMeta.mode, documentMeta.filename, content, getHtmlPreviewRenderWidth])

  // Download dropdown state
  const [isDownloadOpen, setIsDownloadOpen] = useState(false)
  const [isGeneratingRenderedPdf, setIsGeneratingRenderedPdf] = useState(false)
  const downloadRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isDownloadOpen) return
    const closeDownloadDropdown = () => setIsDownloadOpen(false)
    const handlePointerDownOutside = (event: PointerEvent) => {
      const target = event.target
      if (target instanceof Node && downloadRef.current?.contains(target)) return
      closeDownloadDropdown()
    }
    document.addEventListener('pointerdown', handlePointerDownOutside, true)
    window.addEventListener('blur', closeDownloadDropdown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside, true)
      window.removeEventListener('blur', closeDownloadDropdown)
    }
  }, [isDownloadOpen])

  // Download current content as original file format
  const handleDownloadOriginal = useCallback(() => {
    setIsDownloadOpen(false)
    const currentFileContent = currentContentRef.current

    const blob = new Blob([currentFileContent], { type: documentMeta.contentType || 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = buildCanvasDownloadFilename(documentMeta.filename)
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    BetterToast.success('Document downloaded')
  }, [documentMeta.contentType, documentMeta.filename])

  const getDownloadMarkdownContent = useCallback(() => {
    const currentFileContent = currentContentRef.current
    if (!currentFileContent) return ''

    if (documentMeta.mode === 'markdown') {
      return currentFileContent
    }

    const lang = documentMeta.language || ''
    return `\`\`\`${lang}\n${currentFileContent}\n\`\`\``
  }, [documentMeta.mode, documentMeta.language])

  const handleDownloadRenderedPdf = useCallback(async () => {
    setIsDownloadOpen(false)

    try {
      setIsGeneratingRenderedPdf(true)
      const currentHtml = currentContentRef.current || content
      if (!currentHtml) return
      await downloadRenderedCanvasHtmlPdf(currentHtml, {
        filename: documentMeta.filename,
        renderWidth: getHtmlPreviewRenderWidth()
      })
    } catch (err) {
      console.error('PDF generation failed:', err)
      BetterToast.error('Failed to generate PDF')
    } finally {
      setIsGeneratingRenderedPdf(false)
    }
  }, [content, documentMeta.filename, getHtmlPreviewRenderWidth])

  // Download current source/text content as PDF
  const handleDownloadSourcePdf = useCallback(async () => {
    setIsDownloadOpen(false)
    const markdownContent = getDownloadMarkdownContent()
    if (!markdownContent) return

    try {
      const blob = await generatePdfBlob(markdownToHtml(preprocessContent(markdownContent)))
      downloadBlob(blob, buildCanvasDownloadFilename(documentMeta.filename, { extension: 'pdf' }))
    } catch (err) {
      console.error('PDF generation failed:', err)
      BetterToast.error('Failed to generate PDF')
    }
  }, [documentMeta.filename, getDownloadMarkdownContent])

  // Download current content as DOCX
  const handleDownloadDocx = useCallback(async () => {
    setIsDownloadOpen(false)
    const markdownContent = getDownloadMarkdownContent()
    if (!markdownContent) return

    try {
      const processed = preprocessContent(markdownContent)
      const blob = await generateDocxBlob(processed)
      downloadBlob(blob, buildCanvasDownloadFilename(documentMeta.filename, { extension: 'docx' }))
    } catch (err) {
      console.error('DOCX generation failed:', err)
      BetterToast.error('Failed to generate DOCX')
    }
  }, [documentMeta.filename, getDownloadMarkdownContent])

  // Close button handler - warn about unsaved changes
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      // Force save before closing
      debouncedSave.flush()
    }
    onClose()
  }, [hasUnsavedChanges, debouncedSave, onClose])

  // Handle selection feedback from editor - attach canvas metadata
  const handleSelectionFeedback = useCallback(
    (feedback: string, context: BaseSelectionContext) => {
      if (!onSelectionFeedback) return

      const fullContext: CanvasSelectionContext = {
        s3FilePath: canvas.s3FilePath,
        canvasTitle: canvas.title || 'Untitled Document',
        selectedText: context.selectedText,
        contextBefore: context.contextBefore,
        contextAfter: context.contextAfter,
        startLine: context.startLine,
        endLine: context.endLine
      }

      onSelectionFeedback(feedback, fullContext)
    },
    [onSelectionFeedback, canvas.s3FilePath, canvas.title]
  )

  const renderDownloadDropdown = ({
    context,
    buttonClassName = context === 'html-header' ? 'h-8 px-3 text-sm' : 'h-9 px-4'
  }: DownloadDropdownOptions) => {
    const actionById: Record<DownloadMenuActionId, DownloadAction> = {
      'download-rendered-pdf': handleDownloadRenderedPdf,
      'download-original': handleDownloadOriginal,
      'download-source-pdf': handleDownloadSourcePdf,
      'download-docx': handleDownloadDocx
    }
    const resolveDownloadMenuItem = (
      item: DownloadMenuItemDefinition
    ): DownloadMenuItemConfig | null => {
      const itemContext = { context, documentMeta }
      if (!item.contexts.some(c => c === context)) return null
      if (!item.isVisible(itemContext)) return null

      return {
        id: item.id,
        label: item.label(itemContext),
        onClick: actionById[item.actionId]
      }
    }
    const downloadItems = DOWNLOAD_MENU_ITEMS.map(resolveDownloadMenuItem).filter(
      (item): item is DownloadMenuItemConfig => item !== null
    )

    return (
      <div className='relative' ref={downloadRef}>
        <DownloadProgressButton
          type='button'
          onClick={() => !isGeneratingRenderedPdf && setIsDownloadOpen(prev => !prev)}
          disabled={isGeneratingRenderedPdf}
          isLoading={isGeneratingRenderedPdf}
          className={`border border-tokens-neutral-border-1 rounded-xl bg-white text-tokens-neutral-text-primary flex items-center justify-center hover:bg-tokens-neutral-fill-fill-6 disabled:cursor-wait ${buttonClassName}`}
        >
          <span className='flex items-center'>
            <DownloadIcon />
            <span className='ml-2 text-sm'>Download</span>
            <PopoverNavDownIcon className='ml-1' />
          </span>
        </DownloadProgressButton>
        {isDownloadOpen && (
          <SelectorDropdownContent align='start'>
            {downloadItems.map((item, index) => (
              <SelectorDropdownItem
                key={item.id}
                hasDivider={index < downloadItems.length - 1}
                onClick={item.onClick}
              >
                <span className='text-sm text-tokens-neutral-text-primary'>{item.label}</span>
              </SelectorDropdownItem>
            ))}
          </SelectorDropdownContent>
        )}
      </div>
    )
  }
  const downloadMenuContext = getDownloadMenuContext(documentMeta.mode)
  const isHtmlDocument = downloadMenuContext === 'html-header'
  const downloadDropdown = renderDownloadDropdown({ context: downloadMenuContext })
  const saveStatusTooltipClassNames = {
    base: 'before:!bg-tokens-neutral-bg-action',
    content:
      '!bg-tokens-neutral-bg-action !text-tokens-neutral-text-white !rounded-[10px] !px-3 !py-2 shadow-popover'
  }
  const saveStatusTooltipProps = {
    placement: 'top' as const,
    closeDelay: 0,
    classNames: saveStatusTooltipClassNames
  }
  const canvasTitle = canvas.title || 'Untitled Document'

  const renderHtmlViewToggle = () => (
    <SegmentControls
      data-testid='html-view-toggle'
      aria-label='HTML view mode'
      value={htmlViewMode}
      onValueChange={value => setHtmlViewMode(value as 'rendered' | 'source')}
    >
      <SegmentControlsList
        size='sm'
        variant='white'
        className='border-tokens-neutral-border-1 bg-tokens-neutral-fill-fill-4'
      >
        <SegmentControlsTrigger value='rendered' className='min-w-[80px]'>
          Rendered
        </SegmentControlsTrigger>
        <SegmentControlsTrigger value='source' className='min-w-[68px]'>
          Source
        </SegmentControlsTrigger>
      </SegmentControlsList>
    </SegmentControls>
  )

  // Auto-save status: surface what state the document is in alongside a
  // tooltip so users understand "Saved" means "all edits have been
  // persisted to S3 by the auto-save loop", not just "you saved at some
  // point". The "Saved" text label hides on narrow viewports so the
  // header doesn't squeeze the title against the action buttons.
  const renderSaveStatus = () => {
    if (isProcessing) {
      return (
        <CustomTooltip content='The agent is updating this document' {...saveStatusTooltipProps}>
          <div
            data-testid='canvas-save-status'
            data-status='processing'
            className='flex items-center gap-1.5'
          >
            <div className='w-3 h-3 border-2 border-tokens-primary-2 border-t-transparent rounded-full animate-spin' />
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='normal'
              className='text-tokens-status-link-3'
            >
              Processing...
            </Typography>
          </div>
        </CustomTooltip>
      )
    }

    if (isSaving) {
      return (
        <CustomTooltip content='Saving your changes to the cloud…' {...saveStatusTooltipProps}>
          <div
            data-testid='canvas-save-status'
            data-status='saving'
            className='flex items-center gap-1.5'
          >
            <div className='w-2 h-2 rounded-full bg-tokens-status-warning-1 animate-pulse' />
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='normal'
              className='text-tokens-neutral-text-tertiary'
            >
              Saving...
            </Typography>
          </div>
        </CustomTooltip>
      )
    }

    if (hasUnsavedChanges) {
      return (
        <CustomTooltip
          content="You have unsaved edits — they'll be saved automatically in a moment"
          {...saveStatusTooltipProps}
        >
          <div
            data-testid='canvas-save-status'
            data-status='unsaved'
            className='flex items-center gap-1.5'
          >
            <div className='w-2 h-2 rounded-full bg-tokens-status-warning-1' />
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='normal'
              className='text-tokens-neutral-text-tertiary'
            >
              Unsaved
            </Typography>
          </div>
        </CustomTooltip>
      )
    }

    return (
      <CustomTooltip
        content='All changes saved. This document is auto-saved as you type.'
        {...saveStatusTooltipProps}
      >
        <div
          data-testid='canvas-save-status'
          data-status='saved'
          aria-label='All changes saved'
          className='flex items-center gap-1.5 cursor-default'
        >
          <div className='w-2 h-2 rounded-full bg-tokens-status-success-1' />
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            fontWeight='normal'
            className='text-tokens-neutral-text-tertiary hidden md:inline'
          >
            Saved
          </Typography>
        </div>
      </CustomTooltip>
    )
  }

  return (
    <div className='flex flex-col h-full bg-white'>
      {/* Header - matches Figma design.
          gap-4 between the title and actions clusters guarantees breathing
          room when the title gets long enough to truncate; gap-2 inside the
          actions cluster keeps the buttons from running into each other. */}
      <div className='flex items-center justify-between gap-4 px-4 py-3 border-b border-tokens-neutral-border-2 bg-white'>
        <div className='flex items-center gap-3 min-w-0 flex-1'>
          <div data-testid='canvas-title' className='flex-1 min-w-0'>
            <HoverText content={canvasTitle}>
              <Typography
                fontFamily='geist'
                fontSize='titleSm'
                fontWeight='bold'
                className='text-tokens-neutral-text-primary truncate'
              >
                {canvasTitle}
              </Typography>
            </HoverText>
          </div>

          {/* Auto-save status indicator — sits next to the title because it
              describes the document, not an action. */}
          <div className='shrink-0'>{renderSaveStatus()}</div>
        </div>

        {/* Actions */}
        <div className='flex items-center gap-2 shrink-0'>
          {/* HTML gets the compact header dropdown because the rendered iframe
              has no editor toolbar/action slot. */}
          {isHtmlDocument && downloadDropdown}
          {/* Open the live HTML in a new browser tab at full window width.
              Useful when the side panel cannot show the whole layout. */}
          {documentMeta.mode === 'html' && (
            <CustomTooltip
              content='Open this HTML preview in a new browser tab'
              {...saveStatusTooltipProps}
            >
              <Button
                variant='outlined'
                onClick={handleOpenHtmlInNewTab}
                data-testid='html-open-in-new-tab'
                className='h-8 px-3'
                aria-label='Open Render in New Tab'
              >
                <Typography fontFamily='geist' fontSize='bodySm' fontWeight='medium'>
                  Open Render in New Tab
                </Typography>
              </Button>
            </CustomTooltip>
          )}
          {/* HTML render/source toggle - only shown for live HTML canvases */}
          {documentMeta.mode === 'html' && (
            <CustomTooltip
              content={
                htmlViewMode === 'rendered'
                  ? 'Switch to the raw HTML source'
                  : 'Switch back to the rendered preview'
              }
              {...saveStatusTooltipProps}
            >
              {renderHtmlViewToggle()}
            </CustomTooltip>
          )}
          {/* Minimize button */}
          <CustomTooltip content='Close this panel' placement='bottom'>
            <Button
              variant='borderless'
              onClick={handleClose}
              aria-label='Close panel'
              className='h-8 w-8 p-0 text-tokens-neutral-text-tertiary hover:text-tokens-neutral-text-primary'
            >
              <MinimizeIcon />
            </Button>
          </CustomTooltip>
        </div>
      </div>

      {/* Content */}
      <div className='flex-1 min-h-0 overflow-hidden'>
        {isLoading ? (
          <div className='flex items-center justify-center h-full'>
            <div className='flex flex-col items-center gap-3'>
              <div className='w-8 h-8 border-2 border-tokens-primary-2 border-t-transparent rounded-full animate-spin' />
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='normal'
                className='text-tokens-neutral-text-tertiary'
              >
                Loading document...
              </Typography>
            </div>
          </div>
        ) : loadError ? (
          <div className='flex items-center justify-center h-full'>
            <div className='flex flex-col items-center gap-3 text-center px-8'>
              <div className='w-12 h-12 rounded-full bg-tokens-status-error-10 flex items-center justify-center text-tokens-status-error-text'>
                <ErrorIcon />
              </div>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='medium'
                className='text-tokens-status-error-text'
              >
                Failed to load document
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='normal'
                className='text-tokens-neutral-text-tertiary'
              >
                {loadError}
              </Typography>
              <Button variant='outlined' onClick={() => window.location.reload()} className='mt-2'>
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Non-HTML documents render their download dropdown inside the
                editor toolbar so the header stays less crowded. */}
            {documentMeta.mode === 'markdown' && (
              <CanvasEditor
                initialContent={content}
                onContentChange={handleContentChange}
                placeholder='Start editing your document...'
                onSelectionFeedback={onSelectionFeedback ? handleSelectionFeedback : undefined}
                readOnly={isProcessing}
                isProcessing={isProcessing}
                actionSlot={downloadDropdown}
              />
            )}

            {documentMeta.mode === 'code_text' && (
              <CanvasCodeEditor
                initialContent={content}
                onContentChange={handleContentChange}
                language={documentMeta.language}
                placeholder='Start editing your file...'
                onSelectionFeedback={onSelectionFeedback ? handleSelectionFeedback : undefined}
                readOnly={isProcessing}
                isProcessing={isProcessing}
                actionSlot={downloadDropdown}
              />
            )}

            {documentMeta.mode === 'html' &&
              (htmlViewMode === 'rendered' ? (
                // Sandboxed iframe: scripts run, but the opaque origin (no
                // `allow-same-origin`) prevents the embedded page from
                // reaching parent.document / auth state. Never add
                // `allow-same-origin` together with `allow-scripts`.
                <iframe
                  ref={htmlPreviewFrameRef}
                  title={documentMeta.filename}
                  data-testid='html-canvas-iframe'
                  srcDoc={content}
                  sandbox={CANVAS_HTML_SANDBOX}
                  referrerPolicy='no-referrer'
                  className='w-full h-full border-0 bg-white'
                />
              ) : (
                <CanvasCodeEditor
                  initialContent={content}
                  onContentChange={handleContentChange}
                  language='html'
                  placeholder='Edit the HTML source...'
                  onSelectionFeedback={onSelectionFeedback ? handleSelectionFeedback : undefined}
                  readOnly={isProcessing}
                  isProcessing={isProcessing}
                />
              ))}

            {documentMeta.mode === 'unsupported' && (
              <div className='flex flex-col items-center justify-center h-full w-full gap-3 px-8 text-center'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMd'
                  fontWeight='medium'
                  className='text-tokens-neutral-text-primary'
                >
                  This file type can’t be edited inline.
                </Typography>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='normal'
                  className='text-tokens-neutral-text-tertiary'
                >
                  You can still download it for local editing.
                </Typography>
                <div className='mt-2'>{downloadDropdown}</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Icon components
function DownloadIcon() {
  return (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
      <path
        d='M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4.66667 6.66667L8 10L11.3333 6.66667'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M8 10V2'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function MinimizeIcon() {
  return (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
      <path
        d='M3 8H13'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function ErrorIcon() {
  return (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
      <path
        d='M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}
